import React from 'react';
import '../../shared/style.css';
import { Button, Group, Dialog } from '@economic/taco';
import { useField } from 'react-final-form';
import { Field } from '../../../../../shared/components/Form';
import { t } from '../../../../../shared/components/l10n/l10n';
import { InvoicingMessageType, SendByType } from '../../invoices/book-and-send/types';
import { ResendInvoiceFormState } from './ResendInvoiceDialogContainer';
import { SalesDialogFormWithEmailOptionsDrawer } from '../../shared/components/SalesDialogFormWithEmailOptionsDrawer';
import { DisplayMessages } from '../../shared/components/DisplayMessages';
import { useRequiredFieldsValidation } from '../../shared/hooks/useRequiredFieldsValidation';
import { emailTemplateUpdater } from '../../invoices/utils/emailTemplateUpdater';
import { EmailTemplatePicker } from '../../invoices/utils/EmailTemplatePicker';
import { AutoCloseDrawer } from '../../shared/components/AutoCloseDrawer';

interface Props {
    loading: boolean;
    data?: ResendInvoiceFormState;
    open: boolean;
    onHide: () => void;
    onSend: (values: any) => Promise<any>;
}

export function ResendInvoiceDialog(props: Props) {
    const { loading, data, open, onSend, onHide } = props;

    return (
        <SalesDialogFormWithEmailOptionsDrawer
            loading={loading}
            loadingSpinnerWithText
            aria-label={data?.isCreditNote ? t`POPUP_INVOICEETC_RESENDCREDITNOTE` : t`FORM_RESEND_INVOICE_HEADER`}
            open={open}
            onHide={onHide}
            className="book-and-send-dialog form h-[500px]"
            size="xs"
            fullEmailDrawerAttachmentsOptions
            initialData={data}
            onSubmit={onSend}
            hideRemember
            updates={[emailTemplateUpdater(false)]}>
            {(form, drawer, valid) =>
                data && (
                    <>
                        <AutoCloseDrawer drawer={drawer} />
                        <ResendInvoiceDialogContent
                            data={data}
                            onHide={onHide}
                            onSend={() => form.submit()}
                            isFormValid={valid}
                        />
                    </>
                )
            }
        </SalesDialogFormWithEmailOptionsDrawer>
    );
}

interface ResendInvoiceDialogContentProps {
    data: ResendInvoiceFormState;
    onHide: () => void;
    onSend: () => void;
    isFormValid: boolean;
}

function ResendInvoiceDialogContent(props: ResendInvoiceDialogContentProps) {
    const { data, onHide, onSend, isFormValid } = props;
    const messages = useRequiredFieldsValidation();
    const {
        input: { value: sendByType },
    } = useField('sendByType');

    const canSubmit =
        sendByType !== SendByType.undefined && messages.filter(m => m.type === InvoicingMessageType.Error).length === 0;

    return (
        <div className="h-full flex flex-col">
            <Dialog.Title>
                {data?.isCreditNote ? t`POPUP_INVOICEETC_RESENDCREDITNOTE` : t`FORM_RESEND_INVOICE_HEADER`}
            </Dialog.Title>

            <Field.NumberRadioGroup
                name="sendByType"
                options={[
                    {
                        text: t('FORM_RESEND_SEND_BY_EMAIL'),
                        value: SendByType.email,
                    },
                    {
                        text: t('FORM_RESEND_SEND_BY_EINVOICE'),
                        value: SendByType.oioxml,
                    },
                ]}
            />

            <EmailTemplatePicker templates={data.templates} hidePicker={sendByType !== SendByType.email} />

            <div className="book-and-send-dialog__messages">
                <DisplayMessages messages={messages} />
            </div>

            <Dialog.Footer>
                <Group>
                    <Button appearance="discrete" onClick={onHide}>
                        {t('FORM_RESEND_BUTTON_CANCEL')}
                    </Button>
                    <Button appearance="primary" type="submit" onClick={onSend} disabled={!canSubmit || !isFormValid}>
                        {t('FORM_RESEND_BUTTON_SEND')}
                    </Button>
                </Group>
            </Dialog.Footer>
        </div>
    );
}
